import { ssnRegex } from '@dermloop/regular-expressions';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsEmail,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Length,
  Matches,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { SkinTypeDto } from '../clinical-details';
import { GenderDto } from '../clinical-details/gender.dto';
import { ConditionDto } from '../condition/condition.dtos';
import { Person } from '../person.dto';
import { UserDto } from '../user.dto';
import { PatientDetailsDto } from './patient-details.dtos';

export class UpdateHealthRecordConsentDto {
  @IsOptional()
  @IsBoolean()
  studyParticipationConsent?: boolean;

  @IsOptional()
  @IsBoolean()
  applicationImprovementsParticipationConsent?: boolean;

  @IsOptional()
  @IsBoolean()
  anonymisationForEducationalPurposesConsent?: boolean;

  @IsOptional()
  @IsBoolean()
  furtherQuestionsConsent?: boolean;
}

class HealthRecordConsentDto extends UpdateHealthRecordConsentDto {
  @IsOptional()
  @IsString()
  consentsUpdatedDate?: string | Date;
}

export interface ValidatePatientDto {
  person: Person;
  healthRecord: HealthRecordDto;
}

export class CreateHealthRecordDto extends HealthRecordConsentDto {
  @ValidateIf((hr) => !hr.mrn)
  @Matches(ssnRegex, { message: 'Not a valid CPR number' })
  ssn?: string;

  @ValidateIf((hr) => !hr.ssn)
  @IsNotEmpty()
  @IsString()
  mrn?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  firstName?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  lastName?: string;

  @IsOptional()
  @IsDate()
  birthDate?: Date;

  @IsNotEmpty()
  @IsString()
  genderId: string;

  @IsOptional()
  @IsBoolean()
  transgender?: boolean;

  // If true, health record is created as a demo health record
  @IsOptional()
  @IsBoolean()
  demo?: boolean;

  @IsOptional()
  @IsString()
  phoneNumber?: string;

  @IsOptional()
  @IsString()
  phoneNumberPrefix?: string;

  @IsOptional()
  @IsString()
  @Length(1, 250)
  address?: string;

  @IsOptional()
  @IsString()
  @Length(1, 100)
  city?: string;

  @IsOptional()
  @IsString()
  @Length(3, 10)
  postalCode?: string;

  @IsOptional()
  @IsEmail()
  email?: string;
}

export class UpdateHealthRecordDto {
  @IsOptional()
  @IsString()
  phoneNumber?: string;

  @IsOptional()
  @IsString()
  phoneNumberPrefix?: string;

  @IsOptional()
  @IsString()
  @Length(1, 250)
  address?: string;

  @IsOptional()
  @IsString()
  @Length(1, 100)
  city?: string;

  @IsOptional()
  @IsString()
  // Formats vary by country — 3 (IS) to 10 (US ZIP+4) characters
  @Length(3, 10)
  postalCode?: string;

  @IsOptional()
  @IsEmail()
  email?: string;
}

export class SearchHealthRecordRequestDto {
  @IsOptional()
  @Matches(ssnRegex, { message: 'Not a valid CPR number' })
  ssn?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  mrn?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  fhirReference?: string;

  @IsOptional()
  @IsNumber()
  take?: number;

  @IsOptional()
  @IsBoolean()
  ownOnly?: boolean;

  @IsOptional()
  @IsBoolean()
  complete?: boolean;

  // @IsString()
  // @IsNotEmpty()
  // medicalUnitId: string;
}

export class HealthRecordDto extends HealthRecordConsentDto implements BaseDto {
  id: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string | Date;
  updated?: string | Date;
  deleted?: string | Date;

  @IsOptional()
  @Matches(ssnRegex, { message: 'SSN does not match!' })
  ssn?: string;

  mrn?: string;

  firstName?: string;

  lastName?: string;

  birthDate?: Date;

  phoneNumber?: string;

  phoneNumberPrefix?: string;

  address?: string;

  city?: string;

  postalCode?: string;

  email?: string;

  @IsString()
  @IsNotEmpty()
  jurisdictionalGroupId: string;

  @IsBoolean()
  @IsOptional()
  complete?: boolean;

  @IsOptional()
  @IsArray()
  conditions?: ConditionDto[];

  @IsOptional()
  @IsNotEmpty()
  @IsArray()
  /** @deprecated Use conditions */
  lesions?: ConditionDto[];

  @IsOptional()
  @IsArray()
  /** @deprecated Use conditions */
  skinConditions?: ConditionDto[];

  @IsNotEmpty()
  gender: GenderDto;

  @IsOptional()
  @IsBoolean()
  transgender?: boolean;

  @IsNotEmpty()
  @IsString()
  genderId: string;

  @IsOptional()
  @ValidateNested()
  latestPatientDetails?: PatientDetailsDto;

  demo?: boolean;

  // Derived (differenceInYears) — only populated by patient-info projection.
  @IsOptional()
  @IsNumber()
  patientAge?: number;
}

/**
 * Patient-info projection of a health record — the contract of
 * `GET /health-records/v2/:id` (built by `createPatientInfoDto`). Strict `Pick`
 * of {@link HealthRecordDto} so the type reflects exactly what is serialized.
 */
export type PatientInfoDto = Pick<
  HealthRecordDto,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'phoneNumber'
  | 'phoneNumberPrefix'
  | 'address'
  | 'city'
  | 'postalCode'
  | 'email'
  | 'mrn'
  | 'anonymisationForEducationalPurposesConsent'
  | 'applicationImprovementsParticipationConsent'
  | 'furtherQuestionsConsent'
  | 'studyParticipationConsent'
  | 'consentsUpdatedDate'
  | 'created'
  | 'updated'
  | 'deleted'
  | 'birthDate'
  | 'gender'
  | 'genderId'
  | 'ssn'
  | 'latestPatientDetails'
  | 'patientAge'
  | 'jurisdictionalGroupId'
  | 'conditions'
  | 'skinConditions'
  | 'transgender'
>;

/**
 * Minimal health-record embed carried by {@link ConditionSearchResultDto}.
 * Only the identity fields the search projection populates, plus the
 * search-specific `latestSkinType`.
 */
export type ConditionHealthRecordDto = Pick<
  HealthRecordDto,
  'id' | 'ssn' | 'gender'
> & {
  latestSkinType?: SkinTypeDto;
};
